import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Gift, Copy, Share2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { getOrCreateMyReferralCode, buildReferralLink } from "@/lib/referral";

/**
 * Referral share surface — "Convide e ganhe" (R$5 / R$5).
 *
 * Shows the user's code + shareable link with copy / native-share actions and
 * a count of friends who joined. The credit grant itself is server-side and
 * out of scope here (held until the mechanism is approved).
 */
export default function Referral() {
  const navigate = useNavigate();
  const { t, tf } = useLanguage();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState<number | null>(null);
  const [credited, setCredited] = useState<number>(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const c = await getOrCreateMyReferralCode();
      if (!active) return;
      setCode(c);
      setLoading(false);

      // Best-effort stats (RLS lets the referrer read their own rows):
      //   joined   = attributed friends (any non-void status)
      //   credited = friends who paid → reward granted (drives "earned").
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !active) return;
      const { data: rows } = await (supabase as unknown as {
        from: (t: string) => {
          select: (c: string) => {
            eq: (col: string, val: string) => Promise<{ data: { status: string }[] | null }>;
          };
        };
      })
        .from("referrals")
        .select("status")
        .eq("referrer_user_id", user.id);
      if (!active) return;
      const list = rows ?? [];
      setJoined(list.filter((r) => r.status !== "void").length);
      setCredited(list.filter((r) => r.status === "credited").length);
    })();
    return () => { active = false; };
  }, []);

  const link = code ? buildReferralLink(code) : "";

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t("referral.copied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("referral.copyFailed"));
    }
  };

  const handleShare = async () => {
    if (!link) return;
    const shareData = { title: "VYV", text: t("referral.shareText"), url: link };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
      } else {
        await handleCopy();
      }
    } catch {
      /* user dismissed the share sheet — no-op */
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div
        className="max-w-2xl mx-auto px-6 pb-12"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px) + 1rem, 2rem)" }}
      >
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t("common.back")}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2 mb-8"
        >
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-2"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.35)" }}
          >
            <Gift className="w-6 h-6" style={{ color: "hsl(187 72% 55%)" }} />
          </div>
          <h1 className="text-3xl font-display font-medium">{t("referral.title")}</h1>
          <p className="text-muted-foreground">{t("referral.subtitle")}</p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : code ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="space-y-5"
          >
            {/* Code + link card */}
            <div className="vyv-card p-5 space-y-4">
              <div>
                <p className="font-display text-[11px] uppercase tracking-[0.18em] text-foreground/45 mb-1.5">
                  {t("referral.yourCode")}
                </p>
                <p className="font-display text-2xl font-semibold tracking-[0.25em]" style={{ color: "hsl(187 72% 60%)" }}>
                  {code}
                </p>
              </div>

              <div className="rounded-xl px-3 py-2.5 text-sm font-body break-all"
                style={{ background: "hsl(220 35% 12%)", border: "1px solid hsl(0 0% 100% / 0.06)", color: "rgba(255,255,255,0.75)" }}>
                {link}
              </div>

              <div className="flex gap-2.5">
                <button
                  onClick={handleCopy}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 font-display text-[13px] font-medium transition-colors"
                  style={{ background: "hsl(220 35% 14%)", border: "1px solid hsl(0 0% 100% / 0.08)", color: "rgba(255,255,255,0.85)" }}
                >
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                  {copied ? t("referral.copied") : t("referral.copy")}
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 font-display text-[13px] font-semibold transition-transform active:scale-[0.98]"
                  style={{ background: "hsl(187 72% 45%)", color: "#04121a" }}
                >
                  <Share2 size={15} />
                  {t("referral.share")}
                </button>
              </div>
            </div>

            {/* How it works */}
            <div className="vyv-card p-5">
              <p className="font-display text-[13px] font-medium text-foreground/85 mb-1">
                {t("referral.howTitle")}
              </p>
              <p className="font-display text-[12px] text-foreground/55 leading-relaxed">
                {t("referral.howBody")}
              </p>
            </div>

            {joined != null && (
              <div className="grid grid-cols-2 gap-3">
                <div className="vyv-card px-4 py-3.5 text-center">
                  <p className="font-display text-2xl font-semibold text-foreground/90">{joined}</p>
                  <p className="font-display text-[11px] text-foreground/50 mt-0.5">{t("referral.stats.joined")}</p>
                </div>
                <div className="vyv-card px-4 py-3.5 text-center">
                  <p className="font-display text-2xl font-semibold" style={{ color: "hsl(187 72% 60%)" }}>
                    {tf("referral.stats.earnedValue", { amount: String(credited * 5) })}
                  </p>
                  <p className="font-display text-[11px] text-foreground/50 mt-0.5">{t("referral.stats.earned")}</p>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <p className="text-center font-display text-[13px] text-foreground/55 py-8">
            {t("referral.loadError")}
          </p>
        )}
      </div>
    </div>
  );
}
